import { ComponentProps, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography, Popover, Tooltip, ButtonProps } from '@robocorp/components';
import { styled } from '@robocorp/theme';
import { IconArrowUpRight, IconLock } from '@robocorp/icons/iconic';
import useUpgrade from '~/hooks/useUpgrade';

const ContentContainer = styled(Box)`
  cursor: pointer;
`;

const InfoIcon = styled(IconLock)<{ disabled: boolean }>`
  color: ${({ theme }) => theme.color('yellow50')};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  margin: 0 8px;
`;

const UpgradeButton: FC<Omit<ButtonProps, 'icon'>> = ({ ...buttonProps }) => {
  const { t } = useTranslation();
  return (
    <Button icon={IconArrowUpRight} {...buttonProps}>
      {t('upgrade')}
    </Button>
  );
};

interface UpgradeActionProps extends Omit<ButtonProps, 'onClick' | 'icon' | 'disabled'> {
  source: string;
  description?: string;
}

export const UpgradeAction: FC<UpgradeActionProps> = ({ source, description, ...buttonProps }) => {
  const { t } = useTranslation();
  const { onUpgrade, canManageBilling } = useUpgrade(source);

  if (!canManageBilling || description) {
    return (
      <Tooltip
        text={
          <>
            {!!description && <Typography mb={canManageBilling ? 0 : 8}>{description}</Typography>}
            {!canManageBilling && t('upsellContactOwner')}
          </>
        }
      >
        <UpgradeButton onClick={onUpgrade} disabled={!canManageBilling} {...buttonProps} />
      </Tooltip>
    );
  }

  return <UpgradeButton onClick={onUpgrade} {...buttonProps} />;
};

export interface UpgradeInfoProps extends Omit<ComponentProps<typeof Popover>, 'trigger'> {
  source: string;
  description?: string;
}

const UpgradeInfo: FC<UpgradeInfoProps> = ({
  children,
  source,
  description,
  placement = 'left',
  ...popoverProps
}) => {
  const { t } = useTranslation();
  const { onUpgrade, canManageBilling } = useUpgrade(source);

  return (
    <Popover
      trigger={() => (
        <ContentContainer display="flex" justifyContent="center" alignItems="center">
          {children}
          <InfoIcon size={18} disabled={!canManageBilling} />
        </ContentContainer>
      )}
      placement={placement}
      width={330}
      hover
      {...popoverProps}
    >
      <Box display="flex" flexDirection="column">
        <Typography mb={16} lineHeight={1.4}>
          {description ?? t('upgradeInfo.default')}
        </Typography>
        {canManageBilling ? (
          <Box alignSelf={placement !== 'right' ? 'end' : undefined}>
            <UpgradeButton onClick={onUpgrade} />
          </Box>
        ) : (
          <Typography>{t('upsellContactOwner')}</Typography>
        )}
      </Box>
    </Popover>
  );
};

export default UpgradeInfo;
